// ============================================
// COST-RULES. Deterministic compliance + business rules (Wave 3)
// ============================================
// Pure functions. Business constants live here, NOT in prompts.
// HBCF = NSW Fair Trading Home Building Compensation Fund cover.

// DATA-9: parseAmount relocated to cost-math.js (canonical home). Guard
// against circular imports by doing a lazy-safe local parse: cost-math.js
// imports constants from THIS file, so we can't re-import parseAmount from
// it without creating a cycle. Keep the same semantics inline.
function parseAmount(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return isFinite(value) ? value : 0;
    const cleaned = String(value).replace(/[$,\s]/g, '');
    const parsed = parseFloat(cleaned);
    return isFinite(parsed) ? parsed : 0;
}

export const HBCF_THRESHOLD_INCGST = 20_000; // NSW Fair Trading threshold
// Real icare HBCF rate (Peter 2026-06-17: "add the real icare rate; RJ Bird will
// increase if needed"). ~1.1% of contract value is the H04 existing-dwelling /
// renovation all-in premium (icare base 0.927% x 1.10 GST x 1.09 NSW stamp duty,
// per the icare/ATO research). RJ Bird's strata / existing-apartment work (H02)
// is higher (~4.9% all-in) and new-apartment (H03) much higher again — Rodney
// sets a higher per-job rate via config.hbcf.rate / rateBands when warranted.
export const HBCF_DEFAULT_RATE = 0.011;      // ~1.1% (was a flat 10% over-estimate)
export const GST_RATE = 0.10;
export const PRELIMS_THRESHOLD_INCGST = 20_000;

// Statutory insurance / levy lines (HBCF / Home Building Compensation Fund /
// Home Warranty Insurance) are GST-INCLUSIVE disbursements — no GST or markup is
// applied on top. Detection must catch the real spellings, not just the "HBCF"
// acronym: a manually-added "Home Building Compensation Fund Cover" or "Home
// Warranty Insurance" line has no "hbcf" substring, so a narrow /\bhbcf\b/ test
// silently misses it and the committed engine then GST-wraps + marks it up.
// Shared by the committed cost engine (price-items.js), the display engine
// (cost-math-adapter), and the HBCF-presence invariant (cost-math INV5) so the
// three can never drift. Aligned with assistant-tool-handlers ADMIN_BOILERPLATE_RE.
export const STATUTORY_INSURANCE_RE = /\bhbcf\b|home\s+building\s+compensation|home\s+warranty\s+insurance|builders?\s+warranty\s+insurance/i;

/**
 * True when a scope/cost line is a statutory insurance levy that must be treated
 * as a GST-inclusive, no-markup disbursement. Matches an explicit gstExempt flag
 * OR a statutory-insurance name (acronym or full spelling).
 * @param {{ title?: string, id?: string, gstExempt?: boolean, gst_exempt?: boolean }} [item]
 * @returns {boolean}
 */
export function isStatutoryInsuranceLine(item) {
    if (!item) return false;
    const taxTreatment = String(item.taxTreatment || item.tax_treatment || '').trim().toLowerCase();
    if (taxTreatment === 'taxable_service') return false;
    if (item.gstExempt === true || item.gst_exempt === true) return true;
    return STATUTORY_INSURANCE_RE.test(String(item.title || item.id || ''));
}

/**
 * @typedef {Object} HbcfConfig
 * @property {number} [rate] single-rate override
 * @property {number} [thresholdIncGst] eligibility threshold override
 * @property {number} [threshold_inc_gst] eligibility threshold override (persisted alias)
 * @property {Array<{min?:number,max?:number,rate:number}>} [rateBands]
 */

function resolveHbcfRate(subtotalIncGst, hbcfConfig = {}) {
    const bands = Array.isArray(hbcfConfig.rateBands) ? hbcfConfig.rateBands : null;
    if (bands && bands.length) {
        for (const band of bands) {
            const min = Number.isFinite(band.min) ? band.min : -Infinity;
            const max = Number.isFinite(band.max) ? band.max : Infinity;
            if (subtotalIncGst >= min && subtotalIncGst <= max) return band.rate;
        }
    }
    return Number.isFinite(hbcfConfig.rate) ? hbcfConfig.rate : HBCF_DEFAULT_RATE;
}

/**
 * Decide whether HBCF applies and emit a ready-made LineItemInputs for it.
 * @param {{ subtotalIncGst: number, config?: { hbcf?: HbcfConfig } }} args
 * @returns {{ required: boolean, amount: number, rate: number, lineItem: Object|null }}
 */
export function applyHbcf({ subtotalIncGst, config = {} } = {}) {
    const sub = parseAmount(subtotalIncGst);
    const thresholdInput = config?.hbcf?.thresholdIncGst ?? config?.hbcf?.threshold_inc_gst;
    const thresholdNumber = Number(String(thresholdInput ?? '').replace(/[$,\s]/g, ''));
    const thresholdIncGst = thresholdInput !== undefined
        && thresholdInput !== null
        && thresholdInput !== ''
        && Number.isFinite(thresholdNumber)
        && thresholdNumber >= 0
        ? thresholdNumber
        : HBCF_THRESHOLD_INCGST;
    // HBCF is triggered only when the contract price is strictly OVER the
    // threshold. A quote of exactly $20,000 inc GST does not trigger HBCF.
    if (!(sub > thresholdIncGst)) {
        return { required: false, amount: 0, rate: 0, lineItem: null };
    }
    const rate = resolveHbcfRate(sub, config.hbcf);
    // DATA-2 (corrected 2026-06-17 per icare/ATO research): the HBCF premium
    // icare charges the builder is GST-INCLUSIVE — it already contains 10% GST
    // plus 9% NSW stamp duty. It is passed to the homeowner as a disbursement at
    // that full inc-GST figure; QuoteMaker must NOT add another 10% on top (the
    // old code stored hbcfExGst = premium/1.1 then let cost-math re-apply GST,
    // turning a $400 premium into $440 on the invoice). So we store the full
    // premium and pair it with gstExempt:true — which HERE means "do not add GST"
    // (the premium is already GST-inclusive), NOT "this is a GST-free supply". A
    // GST-registered builder may reclaim the premium's GST portion as an input
    // tax credit; the stamp-duty component is not recoverable. Neither changes
    // the quote line. NOTE: `rate` defaults to the current 1.1% all-in single-
    // dwelling advisory. Construction type and builder risk vary, so use
    // `config.hbcf.rate` / `rateBands` for a supported per-job override.
    const hbcfPremium = Math.round(sub * rate * 100) / 100;

    const lineItem = {
        id: 'hbcf-compliance',
        title: 'HBCF. Home Building Compensation Fund Cover',
        hbcfApplicable: false,            // prevent recursive inclusion
        provisionalSum: false,
        // DATA-2: store full premium as ex-GST (no GST on top). Paired with
        // gstExempt:true so cost-math returns gst=0 / incGst=exGst.
        amountOverride: hbcfPremium,
        amountLocked: true,                // user shouldn't edit compliance line
        gstExempt: true,                   // DATA-2: statutory insurance = no GST
        materialsCost: 0,
        labourCost: 0,
        equipmentCost: 0,
        subcontractCost: 0,
        otherCost: hbcfPremium,
        contingencyAmountOverride: 0,
        overheadAmountOverride: 0,
        contingencyPct: 0,
        overheadPct: 0,
        marginPct: 0,
        excludeFromFixedAllocations: true,
    };

    return {
        required: true,
        amount: hbcfPremium,               // DATA-2: invoice shows full premium, no GST wrap
        rate,
        lineItem,
    };
}

/**
 * Whether prelims should be an explicit line on a quote of this size.
 * @param {{ subtotalIncGst: number }} args
 * @returns {boolean}
 */
export function shouldIncludePrelims({ subtotalIncGst } = {}) {
    return parseAmount(subtotalIncGst) >= PRELIMS_THRESHOLD_INCGST;
}
